import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ClickstreamTopLabel, type ClickstreamTopRoute } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { MousePointerClick, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

// ── Clickstream dashboard (Lisa, 2026-07-19) — the VIEWING half of the ──────
// clickstream capture MVP. POST /events/clicks (lib/clickstream.ts) captures
// batches; this founder-only page reads GET /admin/clickstream and renders
// SERVER-SIDE aggregates for one day at a time — no raw event rows ever reach
// the browser. Same Card/SETTLE/MORPH/cn idiom as its Observability/TraceView
// siblings; by-hour strip is hand-rolled divs (house rule: no chart library),
// same spirit as TraceView's MtdSparkline/MagnitudeBar.

const KIND_LABEL: Record<string, string> = { button: 'Button', link: 'Link', other: 'Other' };

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function stepDay(day: string, delta: number): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-white">{value.toLocaleString()}</p>
    </Card>
  );
}

// Hand-rolled by-hour strip — 24 thin bars, pure divs scaled by the day's max
// hour (no chart library). Bottom-aligned in a fixed-height row.
function ByHourStrip({ byHour }: { byHour: number[] }) {
  const max = Math.max(1, ...byHour);
  const stripHeightPx = 64;
  return (
    <div>
      <div className="flex items-end gap-1" style={{ height: stripHeightPx }}>
        {byHour.map((count, hour) => {
          const barHeight = count > 0 ? Math.max(3, Math.round((count / max) * stripHeightPx)) : 1;
          return (
            <div
              key={hour}
              className="flex-1"
              title={`${String(hour).padStart(2, '0')}:00 UTC — ${count} click${count === 1 ? '' : 's'}`}
            >
              <div
                className={cn('w-full rounded-sm', count > 0 ? 'bg-accent-500/70' : 'bg-white/[0.06]')}
                style={{ height: barHeight }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-600">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>23:00</span>
      </div>
    </div>
  );
}

export function ClickstreamPanel() {
  const [day, setDay] = useState<string>(todayUtc());
  const isToday = day >= todayUtc();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['clickstream', day],
    queryFn: () => api.admin.clickstream(day),
  });

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <MousePointerClick className="h-7 w-7 text-accent-500" />
        <div>
          <h1 className="text-2xl font-bold text-white">Clickstream</h1>
          <p className="text-xs text-gray-500">
            What gets clicked, and where — server-side aggregates for one day at a time. No raw
            event rows ever leave the API.
          </p>
        </div>
      </div>

      <div className={SETTLE} style={settleDelay(1)}>
        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setDay(d => stepDay(d, -1))}
              className={cn('btn-ghost px-2', MORPH)}
              title="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input
              type="date"
              value={day}
              max={todayUtc()}
              onChange={e => setDay(e.target.value || todayUtc())}
              className="input font-mono text-xs"
            />
            <button
              onClick={() => setDay(d => stepDay(d, 1))}
              disabled={isToday}
              className={cn('btn-ghost px-2 disabled:cursor-not-allowed disabled:opacity-30', MORPH)}
              title="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            {!isToday && (
              <button onClick={() => setDay(todayUtc())} className="btn-ghost text-xs">
                Today
              </button>
            )}
            <button
              onClick={() => refetch()}
              className={cn('ml-auto btn-ghost px-2', MORPH)}
              title="Refresh"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            </button>
          </div>
        </Card>
      </div>

      {isLoading ? (
        <PageSpinner />
      ) : !data || data.batches === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">
            No clicks captured for <span className="font-semibold text-ink-primary">{day}</span> yet.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-5', SETTLE)} style={settleDelay(2)}>
            <StatTile label="Total clicks" value={data.totalClicks} />
            <StatTile label="Batches" value={data.batches} />
            <StatTile label="Authed users" value={data.actors.authed} />
            <StatTile label="Anon batches" value={data.actors.anonBatches} />
            <StatTile label="Rejected" value={data.rejectedTotal} />
          </div>

          {data.malformedEvents > 0 && (
            <p className="text-xs text-gray-500">
              {data.malformedEvents} event{data.malformedEvents === 1 ? '' : 's'} had unreadable timestamps
            </p>
          )}

          <div className={SETTLE} style={settleDelay(3)}>
            <Card>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Clicks by hour (UTC)
              </p>
              <ByHourStrip byHour={data.byHour} />
            </Card>
          </div>

          <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-2', SETTLE)} style={settleDelay(4)}>
            <Card>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Top buttons</p>
              {data.topLabels.length === 0 ? (
                <p className="text-xs text-gray-600">(no clicks)</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-600">
                      <th className="pb-2 font-semibold">Label</th>
                      <th className="pb-2 font-semibold">Kind</th>
                      <th className="pb-2 text-right font-semibold">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topLabels.map((l: ClickstreamTopLabel, i: number) => (
                      <tr key={`${l.kind}-${l.label}-${i}`} className="border-t border-white/[0.04]">
                        <td className="py-1.5 pr-2 text-gray-300">{l.label}</td>
                        <td className="py-1.5 pr-2 text-gray-500">{KIND_LABEL[l.kind] ?? l.kind}</td>
                        <td className="py-1.5 text-right font-mono text-gray-300">{l.clicks.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <Card>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Top pages</p>
              {data.topRoutes.length === 0 ? (
                <p className="text-xs text-gray-600">(no clicks)</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-600">
                      <th className="pb-2 font-semibold">Route</th>
                      <th className="pb-2 text-right font-semibold">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topRoutes.map((r: ClickstreamTopRoute, i: number) => (
                      <tr key={`${r.route}-${i}`} className="border-t border-white/[0.04]">
                        <td className="max-w-[220px] truncate py-1.5 pr-2 font-mono text-gray-300">{r.route}</td>
                        <td className="py-1.5 text-right font-mono text-gray-300">{r.clicks.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
